'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const exploreLinks = [
  { label: 'Destinations', href: '/search' },
  { label: 'Experiences', href: '/experiences' },
  { label: 'Special Offers', href: '/special-offers' },
  { label: 'Become a Host', href: '/host' },
];

const companyLinks = [
  { label: 'About Ndoto', href: '#' },
  { label: 'Newsroom', href: '#' },
  { label: 'Careers', href: '#' },
  { label: 'Investors', href: '#' },
];

const supportLinks = [
  { label: 'Help Centre', href: '#' },
  { label: 'Cancellation Policy', href: '#' },
  { label: 'Safety Resources', href: '#' },
  { label: 'Accessibility', href: '#' },
];

const socialLinks = [
  {
    label: 'Instagram',
    href: '#',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    ),
  },
  {
    label: 'X / Twitter',
    href: '#',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: '#',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    label: 'YouTube',
    href: '#',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <footer className="relative overflow-hidden" style={{ background: 'var(--color-navy)' }}>
      {/* Decorative top border with brand gradient */}
      <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(64,136,198,0.6) 30%, rgba(245,166,35,0.6) 70%, transparent 100%)' }} />

      {/* Ambient glow blobs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(64,136,198,0.08) 0%, transparent 70%)', transform: 'translateY(-50%)' }} />
      <div className="absolute top-0 right-1/4 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(245,166,35,0.06) 0%, transparent 70%)', transform: 'translateY(-30%)' }} />

      {/* ── UPPER BAND: Brand statement + newsletter ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-12">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          {/* Brand tagline block */}
          <div className="max-w-md">
            <AppLogo variant="light" size={110} />
            <p className="mt-5 text-lg font-semibold leading-snug" style={{ color: 'var(--color-text-primary)' }}>
              Africa's home for<br />
              <span style={{ background: 'linear-gradient(90deg, #4088C6, #F5A623)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                extraordinary stays.
              </span>
            </p>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
              From lakeside lodges in Uganda to rooftop suites in Nairobi — Ndoto connects curious travellers with hosts who make every stay unforgettable.
            </p>
            {/* Social row */}
            <div className="flex items-center gap-2 mt-6">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200"
                  style={{ background: 'rgba(64,136,198,0.12)', border: '1px solid rgba(64,136,198,0.25)', color: 'var(--color-text-secondary)' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(245,166,35,0.15)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(245,166,35,0.4)';
                    (e.currentTarget as HTMLElement).style.color = '#F5A623';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(64,136,198,0.12)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(64,136,198,0.25)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)';
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="w-full lg:w-auto lg:min-w-[360px] rounded-2xl p-6" style={{ background: 'rgba(15,30,53,0.7)', border: '1px solid rgba(64,136,198,0.2)' }}>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#F5A623', boxShadow: '0 0 8px rgba(245,166,35,0.8)' }} />
              <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: '#F5A623' }}>Ndoto Insider</span>
            </div>
            <h3 className="text-base font-bold mt-1 mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Deals before they go public.
            </h3>
            <p className="text-xs mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              Early access to flash sales, new destinations, and host spotlights — straight to your inbox.
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 py-3 px-4 rounded-xl text-sm font-medium" style={{ background: 'rgba(64,136,198,0.1)', border: '1px solid rgba(64,136,198,0.3)', color: '#4088C6' }}>
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                You're in! Watch your inbox.
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 text-sm px-4 py-2.5 rounded-xl outline-none transition-all duration-200"
                  style={{ background: 'rgba(10,22,40,0.8)', border: '1px solid rgba(64,136,198,0.3)', color: 'var(--color-text-primary)' }}
                  onFocus={e => (e.target.style.borderColor = 'rgba(245,166,35,0.6)')}
                  onBlur={e => (e.target.style.borderColor = 'rgba(64,136,198,0.3)')}
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 btn-primary"
                >
                  Join
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ── DIVIDER ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px" style={{ background: 'rgba(64,136,198,0.15)' }} />
      </div>

      {/* ── MIDDLE BAND: Links in asymmetric layout ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Explore — wider */}
          <div className="col-span-2 md:col-span-1">
            <h5 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4088C6' }}>Explore</h5>
            <ul className="space-y-2.5">
              {exploreLinks.map(l => (
                <li key={l.label}>
                  <Link href={l.href} className="text-sm transition-colors duration-150 flex items-center gap-1.5 group" style={{ color: 'var(--color-text-secondary)' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-primary)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
                  >
                    <span className="w-0 group-hover:w-3 h-px transition-all duration-200 flex-shrink-0" style={{ background: '#F5A623' }} />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h5 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4088C6' }}>Company</h5>
            <ul className="space-y-2.5">
              {companyLinks.map(l => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm transition-colors duration-150 flex items-center gap-1.5 group" style={{ color: 'var(--color-text-secondary)' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-primary)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
                  >
                    <span className="w-0 group-hover:w-3 h-px transition-all duration-200 flex-shrink-0" style={{ background: '#F5A623' }} />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h5 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4088C6' }}>Support</h5>
            <ul className="space-y-2.5">
              {supportLinks.map(l => (
                <li key={l.label}>
                  <a href={l.href} className="text-sm transition-colors duration-150 flex items-center gap-1.5 group" style={{ color: 'var(--color-text-secondary)' }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-primary)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
                  >
                    <span className="w-0 group-hover:w-3 h-px transition-all duration-200 flex-shrink-0" style={{ background: '#F5A623' }} />
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Stats pill card */}
          <div className="col-span-2 md:col-span-1">
            <h5 className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#4088C6' }}>By the numbers</h5>
            <div className="space-y-3">
              {[
                { value: '12K+', label: 'Active listings' },
                { value: '8', label: 'African countries' },
                { value: '94%', label: 'Guest satisfaction' },
              ].map(stat => (
                <div key={stat.label} className="flex items-center gap-3 py-2 px-3 rounded-lg" style={{ background: 'rgba(64,136,198,0.08)', border: '1px solid rgba(64,136,198,0.15)' }}>
                  <span className="text-base font-bold font-mono-data" style={{ color: '#F5A623' }}>{stat.value}</span>
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="h-px" style={{ background: 'rgba(64,136,198,0.15)' }} />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Left */}
          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 justify-center sm:justify-start">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>© 2026 Ndoto Technologies Inc.</span>
            <span style={{ color: 'rgba(64,136,198,0.4)' }}>·</span>
            <a href="#" className="text-xs transition-colors duration-150" style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)'}
            >Privacy</a>
            <span style={{ color: 'rgba(64,136,198,0.4)' }}>·</span>
            <a href="#" className="text-xs transition-colors duration-150" style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)'}
            >Terms</a>
            <span style={{ color: 'rgba(64,136,198,0.4)' }}>·</span>
            <a href="#" className="text-xs transition-colors duration-150" style={{ color: 'var(--color-text-muted)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-secondary)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--color-text-muted)'}
            >Sitemap</a>
          </div>

          {/* Right: language + currency */}
          <div className="flex items-center gap-3">
            <button className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition-all duration-150"
              style={{ background: 'rgba(64,136,198,0.1)', border: '1px solid rgba(64,136,198,0.2)', color: 'var(--color-text-secondary)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(245,166,35,0.4)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(64,136,198,0.2)'}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              EN (UG)
            </button>
            <button className="text-xs font-medium px-3 py-1.5 rounded-lg transition-all duration-150"
              style={{ background: 'rgba(64,136,198,0.1)', border: '1px solid rgba(64,136,198,0.2)', color: 'var(--color-text-secondary)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(245,166,35,0.4)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.borderColor = 'rgba(64,136,198,0.2)'}
            >
              UGX
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}